'use client'

import { LineRefreshCcw } from '@qr-front/icons/line-refresh-ccw'
import { LineRefreshCw } from '@qr-front/icons/line-refresh-cw'
import { LineSwapHorizontal } from '@qr-front/icons/line-swap-horizontal'
import { LineSwapVertical } from '@qr-front/icons/line-swap-vertical'
import { Button } from '@qr-front/ui/components/Button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@qr-front/ui/components/Dialog'
import { Slider } from '@qr-front/ui/components/Slider'
import { Spinner } from '@qr-front/ui/components/Spinner'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import Cropper, { Area } from 'react-easy-crop'

import { getCroppedImg, TFlipTransform } from './getCroppedImage'

const normalizeRotation = (rotation: number): number => {
	const rotationAbs = Math.abs(rotation)
	let newRotation = rotation

	if (rotationAbs === 360 || rotationAbs === 0) {
		newRotation = 0
	}

	return newRotation
}

const TransformButton = ({
	icon,
	onClick,
}: {
	icon: ReactElement
	onClick: () => void
}) => (
	<Button
		visual="soft"
		size="icon-large"
		onClick={onClick}
		className="shrink-0 grow [&_svg]:size-5"
	>
		{icon}
	</Button>
)

export const ImageCropDialog = ({
	isOpen,
	setIsOpen,
	originalFile,
	onSave,
	translations,
	aspect = 1,
}: {
	isOpen: boolean
	setIsOpen: (isOpen: boolean) => void
	originalFile: File | null
	onSave: (croppedImage: File) => void
	translations: {
		zoom: string
		closeButton: string
		saveButton: string
	}
	aspect?: number
}) => {
	const [rotation, setRotation] = useState(0)
	const [crop, setCrop] = useState({ x: 0, y: 0 })
	const [zoom, setZoom] = useState(1)
	const [flip, setFlip] = useState<TFlipTransform>({
		horizontal: false,
		vertical: false,
	})
	const [transform, setTransform] = useState('')
	const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(
		null,
	)

	const [fileSrc, setFileSrc] = useState('')
	const [isImageLoading, setIsImageLoading] = useState(true)

	useEffect(() => {
		if (!isOpen || !originalFile) {
			return
		}
		setIsImageLoading(true)
		const url = URL.createObjectURL(originalFile)
		setFileSrc(url)
		return () => URL.revokeObjectURL(url)
	}, [isOpen, originalFile])

	useEffect(() => {
		const verticalFlip = flip.vertical
			? `scaleY(-${zoom})`
			: `scaleY(${zoom})`
		const horizontalFlip = flip.horizontal
			? `scaleX(-${zoom})`
			: `scaleX(${zoom})`
		setTransform(
			`translate(${crop.x}px, ${crop.y}px) rotate(${rotation}deg) ${verticalFlip} ${horizontalFlip}`,
		)
	}, [crop.x, crop.y, flip.horizontal, flip.vertical, rotation, zoom])

	const onMediaLoaded = useCallback(() => {
		setIsImageLoading(false)
	}, [])

	const onCropComplete = useCallback(
		(croppedArea: Area, croppedAreaPixels: Area) => {
			setCroppedAreaPixels(croppedAreaPixels)
		},
		[],
	)

	const handleSave = useCallback(async () => {
		if (croppedAreaPixels && originalFile) {
			const file = await getCroppedImg(
				originalFile,
				croppedAreaPixels,
				rotation,
				flip,
			)
			onSave(file)
		}
	}, [croppedAreaPixels, originalFile, rotation, flip, onSave])

	const handleHorizontalFlip = useCallback(async (): Promise<void> => {
		setFlip((currentFlip) => ({
			...currentFlip,
			horizontal: !currentFlip.horizontal,
		}))
	}, [])

	const handleVerticalFlip = useCallback(async (): Promise<void> => {
		setFlip((currentFlip) => ({
			...currentFlip,
			vertical: !currentFlip.vertical,
		}))
	}, [])

	const handleRotationleft = useCallback(() => {
		setRotation((currentRotation) => {
			return normalizeRotation(currentRotation - 90)
		})
	}, [])

	const handleRotationRight = useCallback(() => {
		setRotation((currentRotation) => {
			return normalizeRotation(currentRotation + 90)
		})
	}, [])

	const transformButtons = (
		<>
			<TransformButton
				icon={<LineSwapHorizontal />}
				onClick={handleHorizontalFlip}
			/>
			<TransformButton
				icon={<LineSwapVertical />}
				onClick={handleVerticalFlip}
			/>
			<TransformButton
				icon={<LineRefreshCcw />}
				onClick={handleRotationleft}
			/>
			<TransformButton
				icon={<LineRefreshCw />}
				onClick={handleRotationRight}
			/>
		</>
	)

	return (
		<Dialog open={isOpen} onOpenChange={setIsOpen}>
			<DialogContent
				closeButtonLabel={translations.closeButton}
				className="p-0"
			>
				<div className="relative h-0 overflow-clip rounded-t-10 pb-[60%]">
					{isImageLoading && (
						<div className="absolute inset-0 z-10 flex items-center justify-center bg-light-blue">
							<Spinner size={48} color="blue" />
						</div>
					)}
					{fileSrc && (
						<Cropper
							image={fileSrc}
							crop={crop}
							zoom={zoom}
							rotation={rotation}
							zoomWithScroll
							minZoom={1}
							maxZoom={5}
							aspect={aspect}
							onCropChange={setCrop}
							onCropComplete={onCropComplete}
							onZoomChange={setZoom}
							onMediaLoaded={onMediaLoaded}
							transform={transform}
							showGrid={false}
							cropShape="rect"
						/>
					)}
				</div>
				<div className="p-6 desktop:p-8">
					<DialogHeader>
						<DialogTitle asChild>
							<h5 className="type-body-medium text-start font-semibold">
								{translations.zoom}
							</h5>
						</DialogTitle>
					</DialogHeader>
					<Slider
						value={[zoom]}
						onValueChange={([value]) => {
							if (value) {
								setZoom(value)
							}
						}}
						min={1}
						max={3}
						step={0.005}
						className="mt-2"
					/>
					<div className="mt-8 flex flex-col flex-wrap gap-6 tablet:flex-row">
						<div className="flex grow gap-2 tablet:grow-0">
							{transformButtons}
						</div>
						<Button
							size="large"
							className="grow"
							onClick={handleSave}
						>
							{translations.saveButton}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}
